
import React, { useState, useMemo } from 'react';
import { Icons } from '../ui/Icons';
import { ReportPermissionLevel, DailyNightAuditReport, MaintenanceRequest, AttendanceRecord } from '../../types';
import { safeFormat } from '../../utils/safeDateFns';

interface DailyOperationReportProps {
    nightAudit?: DailyNightAuditReport[];
    maintenance?: MaintenanceRequest[];
    attendance?: AttendanceRecord[];
    permissionLevel?: ReportPermissionLevel;
    initialLevel?: 'form' | 'preview' | 'history';
    onLevelChange?: (level: 'form' | 'preview' | 'history') => void;
}

const DailyOperationReport: React.FC<DailyOperationReportProps> = ({ 
    nightAudit = [],
    maintenance = [],
    attendance = [],
    permissionLevel = 'view',
    initialLevel = 'form',
    onLevelChange
}) => {
    const canExport = permissionLevel === 'export';

    const [currentLevel, setCurrentLevel] = useState<'form' | 'preview' | 'history'>(initialLevel);
    const [selectedDate, setSelectedDate] = useState(safeFormat(new Date(), 'yyyy-MM-dd'));

    const handleLevelChange = (level: 'form' | 'preview' | 'history') => {
        setCurrentLevel(level);
        if (onLevelChange) onLevelChange(level);
    };

    const dayAudit = useMemo(() => nightAudit.find(r => r.date === selectedDate), [nightAudit, selectedDate]);
    const dayMaintenance = useMemo(() => maintenance.filter(m => m.createdAt.startsWith(selectedDate)), [maintenance, selectedDate]);
    const dayAttendance = useMemo(() => attendance.filter(a => a.date === selectedDate), [attendance, selectedDate]);

    const renderForm = () => (
        <div className="max-w-xl mx-auto">
            <div className="bg-white dark:bg-slate-900 p-8 rounded-[40px] shadow-sm border border-gray-100 dark:border-slate-800 space-y-8">
                <div className="flex items-center gap-4 mb-2">
                    <div className="w-12 h-12 bg-indigo-50 dark:bg-indigo-900/30 rounded-2xl flex items-center justify-center text-indigo-600">
                        <Icons.BarChart className="w-6 h-6" />
                    </div>
                    <div>
                        <h3 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-tight">Daily Operation Summary</h3>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Aggregate Daily Report</p>
                    </div>
                </div>

                <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 px-1">Selected Date</label>
                    <input 
                        type="date" 
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="w-full bg-gray-50 dark:bg-slate-800 border-none rounded-2xl p-4 font-bold text-sm focus:ring-2 focus:ring-indigo-500 transition-all"
                    />
                </div>

                <button 
                    onClick={() => handleLevelChange('preview')}
                    className="w-full bg-indigo-600 text-white py-6 rounded-[32px] font-black uppercase tracking-widest shadow-2xl shadow-indigo-200 dark:shadow-none hover:bg-indigo-700 transition-all flex items-center justify-center gap-3 group"
                >
                    Generate Report
                    <Icons.ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
            </div>
        </div>
    );

    const renderPreview = () => (
        <div className="space-y-6">
            <div className="bg-white dark:bg-slate-900 p-6 rounded-[32px] border border-gray-100 dark:border-slate-800 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 no-print">
                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => handleLevelChange('form')}
                        className="w-10 h-10 rounded-full bg-gray-50 dark:bg-slate-800 flex items-center justify-center hover:bg-gray-100 transition-colors"
                    >
                        <Icons.ArrowLeft className="w-5 h-5 text-gray-600" />
                    </button>
                    <div>
                        <h2 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-tight">Daily Operations</h2>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{selectedDate}</p>
                    </div>
                </div>

                {canExport && (
                    <button 
                        onClick={() => window.print()}
                        className="px-6 py-2 bg-gray-900 text-white rounded-xl text-xs font-bold hover:opacity-90 transition-all flex items-center gap-2"
                    >
                        <Icons.Printer className="w-4 h-4" /> Print
                    </button>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Night Audit Section */}
                <div className="bg-white dark:bg-slate-900 p-8 rounded-[40px] border border-gray-100 dark:border-slate-800 space-y-4">
                    <h3 className="text-xs font-black text-indigo-600 uppercase tracking-widest flex items-center gap-2">
                        <Icons.Moon className="w-4 h-4" /> Night Audit Summary
                    </h3>
                    {dayAudit ? (
                        <div className="grid grid-cols-2 gap-4">
                            <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-2xl">
                                <div className="text-[10px] text-gray-400 font-bold uppercase">In-House</div>
                                <div className="text-xl font-black">{dayAudit.inHouseRooms}</div>
                            </div>
                            <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-2xl">
                                <div className="text-[10px] text-gray-400 font-bold uppercase">Comp</div>
                                <div className="text-xl font-black">{dayAudit.compRooms}</div>
                            </div>
                        </div>
                    ) : (
                        <p className="text-sm text-gray-400 italic">No night audit data for this date.</p>
                    )}
                </div>

                {/* Maintenance Section */}
                <div className="bg-white dark:bg-slate-900 p-8 rounded-[40px] border border-gray-100 dark:border-slate-800 space-y-4">
                    <h3 className="text-xs font-black text-amber-600 uppercase tracking-widest flex items-center gap-2">
                        <Icons.Tool className="w-4 h-4" /> New Work Orders
                    </h3>
                    <div className="space-y-2">
                        {dayMaintenance.length > 0 ? (
                            dayMaintenance.map(req => (
                                <div key={req.id} className="text-xs flex justify-between border-b dark:border-slate-800 pb-2">
                                    <span className="font-bold">{req.requestId}: {req.title}</span>
                                    <span className="text-gray-400 uppercase tracking-tighter">{req.status}</span>
                                </div>
                            ))
                        ) : (
                            <p className="text-sm text-gray-400 italic">No new maintenance requests.</p>
                        )}
                    </div>
                </div>

                {/* Attendance Section */}
                <div className="lg:col-span-2 bg-white dark:bg-slate-900 p-8 rounded-[40px] border border-gray-100 dark:border-slate-800 space-y-4">
                    <h3 className="text-xs font-black text-emerald-600 uppercase tracking-widest flex items-center gap-2">
                        <Icons.UserCheck className="w-4 h-4" /> Staff Attendance
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <div className="p-4 bg-emerald-50 dark:bg-emerald-900/10 rounded-2xl">
                            <div className="text-[10px] text-emerald-600 font-bold uppercase">Scheduled</div>
                            <div className="text-xl font-black">{dayAttendance.length}</div>
                        </div>
                        <div className="p-4 bg-emerald-50 dark:bg-emerald-900/10 rounded-2xl">
                            <div className="text-[10px] text-emerald-600 font-bold uppercase">On Time</div>
                            <div className="text-xl font-black">{dayAttendance.filter(a => a.actualStartTime).length}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            {currentLevel === 'form' ? renderForm() : renderPreview()}
        </div>
    );
};

export default DailyOperationReport;
